import React, { useMemo, useState } from 'react';
import { BrowserRouter as Router, Navigate, NavLink, Route, Routes } from 'react-router-dom';
import { auth, decodeJwt } from './auth';
import DashboardHome from './components/DashboardHome';
import BookDemo from './components/BookDemo';
import GameHistory from './components/GameHistory';
import Login from './components/Login';
import PlayForm from './components/PlayForm';
import PlayList from './components/PlayList';
import { PrivacyPolicy, SupportPage, TermsOfService } from './components/PublicInfoPages';
import RecommendForm from './components/RecommendForm';
import Signup from './components/Signup';
import TeamCoaches from './components/TeamCoaches';
import { getTeamDisplayName } from './teamDisplay';

const baseNavItems = [
  { to: '/', label: 'Start Here', end: true },
  { to: '/plays/new', label: 'Add a Play' },
  { to: '/plays', label: 'View Plays' },
  { to: '/history', label: 'Review Called Plays' },
  { to: '/recommend', label: 'Web Play Call' },
];

function getTeamImageUrl(user = {}) {
  return user?.teamMascotUrl || user?.teamLogoUrl || user?.mascotUrl || user?.logoUrl || '';
}

function AppShell({ user, onTokenChange }) {
  const navItems = user?.isPrimaryCoach
    ? [...baseNavItems, { to: '/team', label: 'Manage Coaches' }]
    : baseNavItems;
  const teamDisplayName = getTeamDisplayName(user);
  const teamImageUrl = getTeamImageUrl(user);

  return (
    <div className="min-h-screen bg-brand-offwhite">
      <div className="mx-auto flex min-h-screen max-w-6xl flex-col px-4 py-4 md:px-6 md:py-6">
        <header className="rounded-lg border border-stone-200 bg-white px-5 py-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <img src="/mick-oc-logo.png" alt="Mick OC" className="h-auto w-40 rounded-lg bg-brand-black" />
              <h1 className="mt-2 text-3xl font-semibold text-brand-black">
                Your real-time offensive coordinator assistant.
              </h1>
              <p className="mt-2 text-base text-stone-600">
                Manage the playbook your staff already teaches, then use Mick OC to recommend calls
                from your own system in real time.
              </p>
            </div>

            <div className="flex items-center gap-3 rounded-lg bg-brand-offwhite px-4 py-3 text-sm text-stone-700">
              {teamImageUrl ? (
                <img
                  src={teamImageUrl}
                  alt={`${teamDisplayName} logo`}
                  className="h-14 w-14 rounded-lg border border-stone-200 bg-white object-contain"
                />
              ) : null}
              <div>
                <div>
                  Team: <span className="font-semibold text-brand-black">{teamDisplayName}</span>
                </div>
                <div className="mt-1">
                  Signed in as {user?.coachName || user?.email || 'coach account'}
                </div>
              </div>
            </div>
          </div>

          <nav className="mt-5 flex flex-wrap gap-2" aria-label="Primary">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `rounded-lg px-4 py-3 text-sm font-semibold transition ${
                    isActive
                      ? 'bg-brand-black text-white'
                      : 'bg-brand-offwhite text-stone-700 hover:bg-brand-orange/10'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}

            <button
              onClick={() => auth.logout()}
              className="ml-auto rounded-lg border border-stone-300 px-4 py-3 text-sm font-semibold text-stone-700 transition hover:bg-brand-offwhite"
            >
              Sign out
            </button>
          </nav>
        </header>

        <main className="mt-5 flex-1">
          <Routes>
            <Route path="/" element={<DashboardHome user={user} />} />
            <Route path="/plays/new" element={<PlayForm />} />
            <Route path="/plays" element={<PlayList />} />
            <Route path="/recommend" element={<RecommendForm />} />
            <Route path="/history" element={<GameHistory />} />
            <Route path="/team" element={<TeamCoaches user={user} onTokenChange={onTokenChange} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function App() {
  const [token, setToken] = useState(auth.getToken());
  const user = useMemo(() => (token ? decodeJwt(token) : null), [token]);

  function handleTokenChange(nextToken) {
    auth.setToken(nextToken);
    setToken(nextToken);
  }

  return (
    <Router>
      <Routes>
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/book-demo" element={<BookDemo />} />
        {user ? (
          <Route path="/*" element={<AppShell user={user} onTokenChange={handleTokenChange} />} />
        ) : (
          <>
            <Route path="/" element={<Login onLogin={() => setToken(auth.getToken())} />} />
            <Route path="/signup" element={<Signup onSignup={() => setToken(auth.getToken())} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
    </Router>
  );
}

export default App;
